import { useState } from 'react';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { isSpecimenCode, localIsoWithOffset, offsetMinutes } from '../../utils/specimenCode';
import QRScanModal from '../QRScanModal';
import QRCodeLabel from '../QRCodeLabel';
import * as S from './styles';

/*
 * In the field, on a phone: one tap mints a specimen's code here and now --
 * date, time and place from the device -- and shows its QR big enough to
 * photograph or print. Or scan a label printed before the trip and put it on
 * this specimen.
 *
 * The device's position is asked for once per tap and never required: no
 * fix (denied, indoors, timed out) mints the code with the site code or UNK
 * instead of waiting.
 *
 * `onCapture(code)`, where the page can take a capture (Field Mode): once a
 * code is made or a label assigned -- or a code made earlier, with no
 * collection yet, is scanned again -- one more tap starts the collection with
 * that code as its tag. The server links the two when the collection is
 * created (routes/specimens.py link_origin), so the plates, slants and DNA
 * made from it carry the code.
 */
const FIX_TIMEOUT_MS = 10000;

export function currentFix() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: Number(pos.coords.latitude.toFixed(6)), lng: Number(pos.coords.longitude.toFixed(6)) }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: FIX_TIMEOUT_MS, maximumAge: 60000 },
    );
  });
}

export default function SpecimenOnSite({ t, onOpen, onCapture }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [details, setDetails] = useState({ location_code: '', taxon_name: '', location_name: '' });
  const [more, setMore] = useState(false);
  const set = (key) => (e) => setDetails((d) => ({ ...d, [key]: e.target.value }));

  const facts = async () => {
    const fix = await currentFix();
    const now = new Date();
    return {
      collected_at: localIsoWithOffset(now),
      utc_offset_minutes: offsetMinutes(now),
      ...(fix ? { gps_lat: fix.lat, gps_lng: fix.lng } : {}),
      ...(details.taxon_name.trim() ? { taxon_name: details.taxon_name.trim() } : {}),
      ...(details.location_name.trim() ? { location_name: details.location_name.trim() } : {}),
      noFix: !fix,
    };
  };

  const mint = async () => {
    setError(''); setResult(null); setBusy('mint');
    try {
      const { noFix, ...body } = await facts();
      const res = await api.post('/specimens', {
        mode: 'on_site', ...body, ...(details.location_code.trim() ? { location_code: details.location_code.trim() } : {}),
      });
      setResult({ specimen: res.data, noFix, kind: 'minted' });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy('');
    }
  };

  const assignScanned = async (raw) => {
    setScanning(false);
    setError(''); setResult(null);
    const code = String(raw || '').trim();
    if (!isSpecimenCode(code)) { setError(t('onSite.notASpecimenLabel', { code })); return; }
    setBusy('assign');
    try {
      const found = await api.get('/lookup', { params: { code } });
      if (found.data?.type !== 'specimen') { setError(t('onSite.notASpecimenLabel', { code })); return; }
      if (found.data.status !== 'reserved') {
        // A code made here whose capture never followed (Back was tapped, the
        // page reloaded) is active with nothing linked: still free to capture
        // under, as Field Mode's tag check also says. Anything else is taken.
        const unlinked = found.data.status === 'active' && !found.data.sample_id && !found.data.external_id;
        setResult({ specimen: { specimen_id: found.data.id, specimen_code: found.data.code, status: found.data.status },
          kind: unlinked ? 'unlinked' : 'inUse' });
        return;
      }
      const { noFix, ...body } = await facts();
      const res = await api.post(`/specimens/${found.data.id}/assign`, body);
      setResult({ specimen: res.data, noFix, kind: 'assigned' });
    } catch (e) {
      setError(e.response?.status === 404 ? t('onSite.unknownLabel', { code }) : errorText(e));
    } finally {
      setBusy('');
    }
  };

  const big = { ...S.btn(), width: '100%', minHeight: 56, fontSize: 17, fontWeight: 600 };
  return (
    <section style={{ ...S.card, maxWidth: 560 }} aria-labelledby="onsite-title" data-testid="specimen-onsite">
      <h3 id="onsite-title" style={S.sectionTitle}>{t('onSite.title')}</h3>
      <p style={S.hint}>{t('onSite.intro')}</p>
      <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
        <button type="button" onClick={mint} disabled={!!busy} style={big} title={t('onSite.tip.mint')}
          data-testid="onsite-mint">{busy === 'mint' ? t('onSite.minting') : t('onSite.mint')}</button>
        <button type="button" onClick={() => setScanning(true)} disabled={!!busy}
          style={{ ...big, background: 'var(--c-info)' }} title={t('onSite.tip.scan')} data-testid="onsite-scan">
          {busy === 'assign' ? t('onSite.assigning') : t('onSite.scan')}
        </button>
        <button type="button" onClick={() => setMore((m) => !m)} aria-expanded={more} style={S.ghostBtn}
          title={t('onSite.tip.more')}>{more ? t('onSite.lessDetails') : t('onSite.moreDetails')}</button>
      </div>
      {more && (
        <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
          <label style={S.label}>
            {t('fields.siteCode')}
            <input value={details.location_code} onChange={set('location_code')} maxLength={12} dir="ltr"
              style={{ ...S.input, ...S.mono, textTransform: 'uppercase', fontSize: 16 }} title={t('tip.siteCode')}
              data-testid="onsite-site" />
            <span style={S.hint}>{t('fields.siteCodeHint')}</span>
          </label>
          <label style={S.label}>
            {t('fields.taxon')}
            <input value={details.taxon_name} onChange={set('taxon_name')} maxLength={300}
              style={{ ...S.input, fontSize: 16 }} title={t('tip.taxon')} />
          </label>
          <label style={S.label}>
            {t('fields.locationName')}
            <input value={details.location_name} onChange={set('location_name')} maxLength={300}
              style={{ ...S.input, fontSize: 16 }} title={t('tip.locationName')} />
          </label>
        </div>
      )}
      {error && <p style={S.error} role="alert">{error}</p>}
      {result && (
        <div style={{ marginTop: 16, textAlign: 'center' }} data-testid="onsite-result" role="status">
          <p style={{ margin: '0 0 8px', fontWeight: 600 }}>{t(`onSite.result.${result.kind}`)}</p>
          {/* A scanned code is on the label in hand already: no second QR. */}
          {['minted', 'assigned'].includes(result.kind) && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {/* The short lab code (L-00042) prints as text under the QR, which stays the ISO code. */}
              <QRCodeLabel value={result.specimen.specimen_code} size={200} caption={result.specimen.lab_code || ''} />
            </div>
          )}
          {['inUse', 'unlinked'].includes(result.kind) && (
            <div dir="ltr" style={{ ...S.mono, fontSize: 18, fontWeight: 700 }}>{result.specimen.specimen_code}</div>
          )}
          {result.noFix && <p style={S.hint}>{t('onSite.noFix')}</p>}
          {/* Not for a label already in use: that specimen is someone's already. */}
          {onCapture && ['minted', 'assigned', 'unlinked'].includes(result.kind) && (
            <button type="button" style={{ ...big, marginTop: 12 }} title={t('onSite.tip.capture')}
              onClick={() => onCapture(result.specimen.specimen_code)} data-testid="onsite-capture">
              {t('onSite.capture')}
            </button>
          )}
          {onOpen && (
            <button type="button" style={{ ...S.ghostBtn, marginTop: 8 }} title={t('tip.open')}
              onClick={() => onOpen(result.specimen.specimen_id)} data-testid="onsite-open">{t('onSite.open')}</button>
          )}
        </div>
      )}
      <QRScanModal open={scanning} onDetected={assignScanned} onClose={() => setScanning(false)} />
    </section>
  );
}
